import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { ApiError } from '../lib/api';
import { Button } from './controls';

/** Hinweis-Zeile, Fehleranzeige, Toast (design.md §7, Zustände). */

export function Notice({ tone = 'err', children }: { tone?: 'err' | 'warn' | 'info'; children: ReactNode }) {
  return (
    <div className={`notice ${tone === 'err' ? '' : tone}`} role={tone === 'err' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

/** Fehler: Klartext + Code in Mono + Request-ID (Abgleich mit Logs) + „Erneut versuchen“. */
export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const e = error instanceof ApiError ? error : null;
  return (
    <Notice>
      <span>{e?.message ?? (error instanceof Error ? error.message : 'Unbekannter Fehler.')}</span>
      {e && (
        <span className="muted">
          <span className="mono">{e.code}</span>
          {e.requestId && (
            <>
              {' · Request '}
              <span className="mono">{e.requestId}</span>
            </>
          )}
        </span>
      )}
      {onRetry && (
        <span>
          <Button variant="quiet" onClick={onRetry} className="inline">
            Erneut versuchen
          </Button>
        </span>
      )}
    </Notice>
  );
}

/** Erfolgsmeldung nach Aktionen, unten rechts, 4 s. Fehler gehören in eine Hinweis-Zeile, nicht in einen Toast. */
const ToastContext = createContext<(text: string) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<{ id: number; text: string }[]>([]);
  const show = useCallback((text: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="toast">
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
